import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Users, Handshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const partnerItems = [
  {
    id: 'acp',
    title: { 
      de: 'Schulungen für Mitarbeitende zu den Themen IT-Security und KI-Awareness', 
      en: 'Training for Employees on IT-Security and AI-Awareness' 
    },
    categories: ['unterweisungssystem', 'awareness-trainings'],
    logo: '/assets/partners/acp-logo.png',
    hasDetailPage: true,
    detailLink: '/partner/acp'
  },
  {
    id: 'arinco',
    title: { 
      de: 'arinco – Kompetenzpartner für Arbeitssicherheit', 
      en: 'arinco – Competence Partner for Workplace Safety' 
    },
    categories: ['unterweisungssystem', 'arbeitssicherheit'],
    logo: '/assets/partners/arinco-logo.png',
    hasDetailPage: true,
    detailLink: '/partner/arinco'
  },
  {
    id: 'siemens',
    title: { 
      de: 'Siemens – Partner für integrierte Zutritts- und Unterweisungslösungen', 
      en: 'Siemens – Partner for Integrated Access and Instruction Solutions' 
    },
    categories: ['besucherunterweisung', 'unterweisungssystem'],
    logo: '/assets/partners/siemens.png',
    hasDetailPage: true,
    detailLink: '/partner/siemens'
  },
  {
    id: 'wifi-ooe',
    title: { 
      de: 'WIFI Oberösterreich – Partner in der Ausbildung von Sicherheitsfachkräften', 
      en: 'WIFI Upper Austria – Partner in Safety Officer Training' 
    },
    categories: ['arbeitssicherheit'],
    logo: '/assets/partners/wifi-logo.jpg',
    hasDetailPage: true,
    detailLink: '/partner/wifi-ooe'
  }
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

export default function PartnerPage() {
  const { language } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('partner');

  const categories = [
    { id: 'all', label: language === 'de' ? 'Alle' : 'All' },
    { id: 'unterweisungssystem', label: language === 'de' ? 'Unterweisungssystem' : 'Instruction System' },
    { id: 'besucherunterweisung', label: language === 'de' ? 'Besucherunterweisung' : 'Visitor Instruction' },
    { id: 'arbeitssicherheit', label: language === 'de' ? 'Arbeitssicherheit' : 'Workplace Safety' },
    { id: 'awareness-trainings', label: 'Awareness-Trainings' }
  ];

  const filteredItems = activeCategory === 'all'
    ? partnerItems
    : partnerItems.filter(item => item.categories.includes(activeCategory));

  const getCategoryLabels = (item) => {
    return item.categories
      .map(catId => categories.find(c => c.id === catId)?.label)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div className="min-h-screen pt-20" data-testid="partner-page">
      <SEO
        title={language === 'de' ? 'Partner - E-Learning & Arbeitssicherheit' : 'Partners - E-Learning & Workplace Safety'}
        description={language === 'de' 
          ? 'Unsere Partner im Bereich E-Learning, Arbeitssicherheit und Awareness-Trainings: arinco, ACP, WIFI Oberösterreich, Siemens. Gemeinsam für sichere Arbeitsplätze.' 
          : 'Our partners in e-learning, workplace safety and awareness training: arinco, ACP, WIFI Upper Austria, Siemens. Together for safe workplaces.'}
        keywords={language === 'de'
          ? 'E-Learning Partner, Arbeitssicherheit Partner, arinco, ACP, WIFI Oberösterreich, Siemens, Awareness Training Partner, Sicherheitsfachkräfte Ausbildung, Besucherunterweisung, Zutrittssteuerung'
          : 'E-Learning Partners, Workplace Safety Partners, arinco, ACP, WIFI Upper Austria, Siemens, Awareness Training Partners, Safety Officer Training, Visitor Instruction, Access Control'}
        path="/partner"
        lang={language}
      />
      
      <section className="py-20 md:py-28 bg-[#E7F0FA]" data-testid="partner-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0D2440] mb-6">
              {language === 'de' ? 'Partner & Kunden' : 'Partners & Clients'}
            </h1>
            <p className="text-lg text-[#5A7A9B] max-w-3xl mx-auto mb-10">
              {language === 'de'
                ? 'Gemeinsam mit unseren Partnern entwickeln wir innovative Lösungen für Unterweisungen, Arbeitssicherheit und Awareness-Trainings.'
                : 'Together with our partners, we develop innovative solutions for training, workplace safety and awareness programs.'}
            </p>

            {/* Partner / Kunden Toggle */}
            <div className="inline-flex bg-white rounded-full p-1 shadow-md border border-[#C8D8E8]" data-testid="partner-kunde-toggle">
              <button
                onClick={() => setActiveTab('partner')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  activeTab === 'partner'
                    ? 'bg-[#0D2440] text-white shadow-sm'
                    : 'text-[#5A7A9B] hover:text-[#0D2440]'
                }`}
                data-testid="tab-partner"
              >
                <Handshake className="w-4 h-4" />
                {language === 'de' ? 'Partner' : 'Partners'}
              </button>
              <button
                onClick={() => setActiveTab('kunden')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  activeTab === 'kunden'
                    ? 'bg-[#0D2440] text-white shadow-sm'
                    : 'text-[#5A7A9B] hover:text-[#0D2440]'
                }`}
                data-testid="tab-kunden"
              >
                <Users className="w-4 h-4" />
                {language === 'de' ? 'Kunden' : 'Clients'}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {activeTab === 'kunden' ? (
        <section className="py-20 md:py-28 bg-white" data-testid="kunden-coming-soon">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div {...fadeUp}>
              <div className="w-20 h-20 bg-[#E7F0FA] rounded-2xl flex items-center justify-center mx-auto mb-8">
                <Users className="w-10 h-10 text-[#2E5E99]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0D2440] mb-4">
                {language === 'de' ? 'Kommt bald' : 'Coming Soon'}
              </h2>
              <p className="text-lg text-[#5A7A9B] mb-8">
                {language === 'de'
                  ? 'Unsere Kundenreferenzen werden in Kürze hier veröffentlicht. Schauen Sie bald wieder vorbei!'
                  : 'Our client references will be published here shortly. Check back soon!'}
              </p>
              <Link to="/contact" data-testid="kunden-contact-cta">
                <Button className="bg-[#0D2440] hover:bg-[#162D4A] text-white h-12 px-8 text-base">
                  {language === 'de' ? 'Kontakt aufnehmen' : 'Contact Us'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      ) : (
        <>
      <section className="py-20 md:py-28 bg-white" data-testid="partner-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#2E5E99] text-white'
                    : 'bg-[#E7F0FA] text-[#5A7A9B] hover:bg-[#D0E0F0]'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <Link 
                key={item.id}
                to={item.detailLink}
                data-testid={`partner-item-${item.id}`}
              >
                <motion.div
                  className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#C8D8E8] cursor-pointer h-full"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.1, 0.5) }}
                >
                  <div className="aspect-[4/3] overflow-hidden relative bg-white flex items-center justify-center p-8">
                    <img
                      src={item.logo}
                      alt={item.title[language]}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold tracking-wider text-[#2E5E99] uppercase">
                      {getCategoryLabels(item)}
                    </span>
                    <h3 className="font-semibold text-lg text-[#0D2440] mt-2 group-hover:text-[#2E5E99] transition-colors line-clamp-2">
                      {item.title[language]}
                    </h3>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#2E5E99]" data-testid="partner-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {language === 'de' ? 'Partner werden' : 'Become a Partner'}
            </h2>
            <p className="text-lg text-white/80 mb-10">
              {language === 'de'
                ? 'Interessiert an einer Partnerschaft? Kontaktieren Sie uns und lassen Sie uns gemeinsam innovative Lösungen entwickeln.'
                : 'Interested in a partnership? Contact us and let\'s develop innovative solutions together.'}
            </p>
            <Link to="/contact" data-testid="partner-contact-cta">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 h-12 px-8 text-base">
                {language === 'de' ? 'Kontakt aufnehmen' : 'Contact Us'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
        </>
      )}
    </div>
  );
}
